import React, { useEffect, useState } from "react";
import { Row, Table, DatePicker, Space, message, Spin } from "antd";
import { GlobalTableStyle, LightBlueContainer, WhiteContainer } from "../../styled/Container.styled";
import { ColumnsType } from "antd/es/table";
import { useLocation, useNavigate } from "react-router-dom";
import pageSize from "../../../constants/pageSize";
import {
  useGetAllTransactionsForMonthQuery,
  useRequestTransactionViewOtpMutation,
  useVerifyTransactionViewOtpMutation,
} from "../../../feature/Transactions/transactionApiSlice";
import { ViewDetailsButton, RefreshButton, PrimaryButton } from "../../styled/Button.styled";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import dayjs from "dayjs";
import { Empty } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import InlineOtpVerifyPanel from "../../Common/InlineOtpVerifyPanel";
const { RangePicker } = DatePicker;

type PendingDownload = "invoice" | "excel" | null;

export default function Transaction() {
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonthYear());
  const [isDownloading, setIsDownloading] = useState<boolean>(false); // State to track download status
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Per-download OTP gate: every report download (invoice zip or excel) requires a
  // fresh OTP sent to the admin's registered number, verified right before the file
  // is fetched. Distinct from the page-level view gate on viewTransactions.tsx.
  const [requestTransactionViewOtp] = useRequestTransactionViewOtpMutation();
  const [verifyTransactionViewOtp] = useVerifyTransactionViewOtpMutation();
  const [pendingDownload, setPendingDownload] = useState<PendingDownload>(null);
  const [isRequestingDownloadOtp, setIsRequestingDownloadOtp] = useState(false);
  const [isVerifyingDownloadOtp, setIsVerifyingDownloadOtp] = useState(false);
  const [downloadOtpError, setDownloadOtpError] = useState("");
  const { data, isLoading: isTransactionsLoading, refetch: fetchTransactions } = useGetAllTransactionsForMonthQuery(selectedMonth);
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const openPlaceDetails = (record: any) => {
    navigate(`view/${record.tripId}?page=${currentPage}`, { state: { transaction: record } });
  };

  useEffect(() => {
    // Restore page from URL when component mounts or when navigating back
    const urlParams = new URLSearchParams(routerLocation.search);
    const pageFromUrl = urlParams.get('page');
    if (pageFromUrl) {
      setCurrentPage(parseInt(pageFromUrl, 10));
    }
  }, [routerLocation.search]);

  const columns: ColumnsType<any> = [
    {
      title: "Name",
      dataIndex: "driverName",
      key: "driverName",
    },
    {
      title: "Payment ID",
      dataIndex: "paymentId",
      key: "paymentId",
    },
    {
      title: "Amount",
      dataIndex: "transactionAmount",
      key: "transactionAmount",
    },
    {
      title: "Mode",
      dataIndex: "paymentMode",
      key: "paymentMode",
    },
    {
      title: "Trip Type",
      dataIndex: "tripType",
      key: "tripType",
    },
    {
      title: "Transaction Date",
      dataIndex: "dateOfTransaction",
      key: "dateOfTransaction",
    },
    {
      title: "Transaction Time",
      dataIndex: "timeOfTransaction",
      key: "timeOfTransaction",
    },
    {
      key: "viewDetails",
      dataIndex: "viewDetails",
      render: (_, record) => (
        <ViewDetailsButton onClick={() => openPlaceDetails(record)}>
          View Details
        </ViewDetailsButton>
      ),
    },
  ];

  const handleMonthChange = (date: any) => {
    if (date) {
      const selectedDate = date.format("YYYY-MM");
      setSelectedMonth(selectedDate);
      fetchTransactions();
    }
  };

  const requestDownloadOtp = async () => {
    try {
      setIsRequestingDownloadOtp(true);
      setDownloadOtpError("");
      await requestTransactionViewOtp().unwrap();
      message.success("OTP sent to your registered phone number");
    } catch (error) {
      console.error("Error requesting download OTP:", error);
      message.error("Failed to send OTP. Please try again.");
    } finally {
      setIsRequestingDownloadOtp(false);
    }
  };

  const startDownload = (type: Exclude<PendingDownload, null>) => {
    // OTP disabled until the OTP SMS template is registered on DLT
    // setPendingDownload(type);
    // requestDownloadOtp();
    if (type === "invoice") {
      performInvoiceDownload();
    } else {
      performExcelDownload();
    }
  };

  const cancelDownloadOtp = () => {
    setPendingDownload(null);
    setDownloadOtpError("");
  };

  const handleVerifyDownloadOtp = async (otp: string) => {
    if (!pendingDownload) return;
    try {
      setIsVerifyingDownloadOtp(true);
      setDownloadOtpError("");
      await verifyTransactionViewOtp(otp).unwrap();
      const type = pendingDownload;
      setPendingDownload(null);
      if (type === "invoice") {
        await performInvoiceDownload();
      } else {
        await performExcelDownload();
      }
    } catch (error) {
      console.error("Error verifying download OTP:", error);
      setDownloadOtpError("Invalid or expired OTP. Please try again.");
    } finally {
      setIsVerifyingDownloadOtp(false);
    }
  };

  const performInvoiceDownload = async () => {
    setIsDownloading(true);
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || "https://himcab.prod.service.ripple-anubudh.tech/api";
      const apiUrl = `${baseUrl}/admin/transaction/download-invoices?month=${selectedMonth}`;
      const headerToken = secureLocalStorage.getItem("token");
      if (!headerToken) {
          message.error("Authorization token is missing.");
          setIsDownloading(false);
          return;
      }

      const response = await axios.get(apiUrl, {
          headers: {
              Authorization: `Bearer ${headerToken}`,
          },
          responseType: "blob", 
      });

      if (response.status === 200) {
          const contentDisposition = response.headers['content-disposition'];
          let fileName = 'download.zip'; 

          if (contentDisposition) {
              const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (fileNameMatch != null && fileNameMatch[1]) {
                  fileName = fileNameMatch[1].replace(/['"]/g, '');
                  if (!fileName.endsWith('.zip')) {
                      fileName += '.zip'; 
                  }
              }
          }

        const blob = new Blob([response.data], {
          type: "application/zip",
        });

        const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName); 
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          message.success("Invoices are downloading.");
      } else {
          throw new Error("Failed to download invoices.");
      }
  } catch (error) {
      message.error("Failed to download invoices.");
      console.error("Failed to download invoices:", error);
  }
  setIsDownloading(false); 
};

  const performExcelDownload = async () => {
  setIsDownloading(true);

  try {
    const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || "https://himcab.prod.service.ripple-anubudh.tech/api";
    const excelApiUrl = `${baseUrl}/admin/transaction/download-invoices-excel?month=${selectedMonth}`;
    const headerToken = secureLocalStorage.getItem("token");

    if (!headerToken) {
      message.error("Authorization token is missing.");
      setIsDownloading(false);
      return;
    }

    const response = await axios.get(excelApiUrl, {
      headers: {
        Authorization: `Bearer ${headerToken}`,
      },
      responseType: "blob",
    });

    if (response.status === 200) {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `report_${selectedMonth}.xlsx`);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      message.success("Excel file is downloading.");
    } else {
      throw new Error("Failed to download Excel file.");
    }
  } catch (error) {
    console.error("Download Excel Error:", error);
    message.error("Failed to download Excel file.");
  }

  setIsDownloading(false);
};

  return (
    <>
    <div style={{ position: "relative" }}>
      <LightBlueContainer>
        <Row justify="end" className="button-bottom-margin">
          <Space>
            <RefreshButton 
              loading={isRefreshing}
              onClick={async () => {
                setIsRefreshing(true);
                await fetchTransactions();
                setIsRefreshing(false);
              }} 
            />
            <DatePicker
              picker="month"
              format="YYYY-MM"
              onChange={handleMonthChange}
              defaultValue={dayjs(selectedMonth, "YYYY-MM")}
              style={{ height: "3rem", fontSize: "20px" }}
            />
            <PrimaryButton
              onClick={() => startDownload("invoice")}
              disabled={isDownloading}
            >
              Download Invoice
            </PrimaryButton>
            <PrimaryButton
              onClick={() => startDownload("excel")}
              disabled={isDownloading}
            >
              Download Excel Report
            </PrimaryButton>
          </Space>
        </Row>
       

<WhiteContainer>
  <GlobalTableStyle>
    {isDownloading ? (
      <Spin tip="Downloading...">
        <Table<any>
          loading={isTransactionsLoading}
          columns={columns}
          dataSource={data?.data.content.map((item: any) => ({
            ...item,
            key: item.transactionId,
          }))}
          pagination={{ 
            pageSize,
            current: currentPage,
            onChange: (page) => {
              setCurrentPage(page);
            },
          }}
          locale={{
            emptyText: (
              <div
                style={{
                  textAlign: "center",
                  padding: "50px 0",
                }}
              >
                <InboxOutlined style={{ fontSize: 50, color: "#d9d9d9" }} />
                <div
                  style={{
                    marginTop: 100,
                    fontSize: 16,
                    color: "#999",
                  }}
                >
                  No Data
                </div>
              </div>
            ),
          }}
        />
      </Spin>
    ) : (
      <Table<any>
        className="transaction-table"
        loading={isTransactionsLoading}
        columns={columns}
        dataSource={data?.data.content.map((item: any) => ({
          ...item,
          key: item.transactionId,
        }))}
        pagination={{ 
          pageSize,
          current: currentPage,
          onChange: (page) => {
            setCurrentPage(page);
          },
        }}
        locale={{
          emptyText: (
            <div
              style={{
                textAlign: "center",
                padding: "250px 0",
              }}
            >
              <InboxOutlined style={{ fontSize: 50, color: "#d9d9d9" }} />
              <div
                style={{
                  marginTop: 10,
                  fontSize: 16,
                  color: "#999",
                }}
              >
                No Data Found
              </div>
            </div>
          ),
        }}
      />
    )}
  </GlobalTableStyle>
</WhiteContainer>

      </LightBlueContainer>
      {pendingDownload && (
        <InlineOtpVerifyPanel
          title="Verify OTP to Download Report"
          description={
            downloadOtpError ||
            "An OTP has been sent to the admin's registered phone number. Enter it below to download this report."
          }
          isVerifying={isVerifyingDownloadOtp || isRequestingDownloadOtp}
          verifyButtonLabel="Verify & Download"
          onVerify={handleVerifyDownloadOtp}
          onCancel={cancelDownloadOtp}
          onResend={requestDownloadOtp}
        />
      )}
    </div>
    </>
  );
}

function getCurrentMonthYear() {
  const date = new Date();
  const year = date.getFullYear();
  let month: string | number = date.getMonth() + 1;
  month = month < 10 ? "0" + month : month;
  return `${year}-${month}`;
}
